import { FC, Suspense, useEffect, useMemo, useRef } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router';
import { Container } from '@mui/material';
import { format } from 'date-fns';

import { useQueryState, useRouter } from '@cvt/routing';
import ErrorBoundary from '@cvt/components/ErrorBoundary';
import { BodyLoading } from '@cvt/components/layout/BodyLoading';
import { DEFAULT_QUERY_LIMIT } from '@cvt/helpers/query';

import { CreateMeeting } from './CreateMeeting';
import { EditMeeting } from './EditMeeting';
import { ViewMeetings } from './ViewMeetings';


export const Meetings: FC = () => {
  const router = useRouter();
  const location = useLocation();

  const prevPathname = useRef(location.pathname);

  const [status, setStatus] = useQueryState<Meetings.MeetingStatus>('status');
  const [tags, setTags] = useQueryState<string[]>('tags');
  const [search, setSearch] = useQueryState<string>('search');
  const [dateGte, setDateGte] = useQueryState<Date | string>('start-date');
  const [dateLte, setDateLte] = useQueryState<Date | string>('end-date');

  const filters = useMemo(() => ({
    status,
    tags,
    limit: DEFAULT_QUERY_LIMIT,
    search,
    dateGte,
    dateLte,
  }), [status, tags, search, dateGte, dateLte]);

  const setters = useMemo(() => ({
    status: setStatus,
    tags: setTags,
    search: setSearch,
    dateGte: setDateGte,
    dateLte: setDateLte,
  }), [setStatus, setTags, setSearch, setDateGte, setDateLte]);

  const disabledFilters = useMemo(() => {
    return [];
  }, []);

  useEffect(() => {
    const dateGteDate = dateGte ? new Date(dateGte + 'T00:00:00.000') : null;
    const dateLteDate = dateLte ? new Date(dateLte + 'T00:00:00.000') : null;
    const today = new Date(format(new Date(), 'yyyy-MM-dd') + 'T00:00:00.000');

    if (location.pathname.includes('upcoming')) {
      if (!dateGteDate || dateGteDate < today) {
        setDateGte(format(today, 'yyyy-MM-dd'));
      }

      if (dateLteDate) {
        setDateLte(undefined);
      }
    } else if (location.pathname.includes('past')) {
      if (dateGteDate) {
        setDateGte(undefined);
      }

      if (!dateLteDate || dateLteDate > today) {
        setDateLte(format(today, 'yyyy-MM-dd'));
      }
    }

    if (
      location.pathname.includes('all') &&
    (prevPathname.current.includes('upcoming') || prevPathname.current.includes('past'))
    ) {
      setDateGte(undefined);
      setDateLte(undefined);
    }

    prevPathname.current = location.pathname;
  }, [dateGte, dateLte, location.pathname, setDateGte, setDateLte]);

  return (
    <Container maxWidth="lg" sx={{ mt: 2, m: 0, width: '100%' }} disableGutters>
      <ErrorBoundary>
        <Suspense fallback={<BodyLoading height="100%"/>}>
          <Routes>
            <Route
              path="/list/*"
              element={<ViewMeetings filters={filters} setters={setters} disabledFilters={disabledFilters}/>}
            />
            <Route
              path="/create"
              element={<CreateMeeting/>}
            />
            <Route
              path="/edit/:meetingId"
              element={<EditMeeting/>}
            />
            <Route path="*" element={<Navigate to={router.meetings.list.path} replace/>}/>
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </Container>
  );
};
